/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesAdministrativeLaw";
import personFields from "../modules/personFields";
import { dateFieldDefault } from "../../../validationRules";
import addressFields from "../modules/addressFields";
import { hasActiveCourtAppointment } from "../../ProductService/productService";
import { allDataPageCourtFields } from "../allDataPageCourtFields";
import {
  addProcessParticipant,
  aiConcernDescription,
  caseInsurance,
  extraJudicialProcedure,
  getAdditionalClientsForm,
  getNextInstanceSection,
  getUnderageClientForm,
  judicialProcedure,
  opponentLawyer,
  processParticipantsAuthority,
  processParticipantsOrganizations,
  processParticipantsPeople,
  prosecution
} from "../abstractLitigationCase/fullFormDefinition";
import { BackofficeUser } from "../../../../types/BackofficeUser";
import { getCivilServiceLawCaseRejectionCategorySelectOptions } from "./civilServiceLawFormHelperService";
import { insertAfterPath } from "../../../formServiceFunctions";
import _ from "lodash";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { SelectOption } from "../../../../types/FormDefinitions/FullFormDefinition";
import { CivilServiceLaw } from "../../../../types/Products/AdministrativeLaw/CivilServiceLaw/CivilServiceLaw";

import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";

export const fullFormDefinition = (product: CivilServiceLaw, currentUser: BackofficeUser) => {
  const incidentBaseCreated = {
    isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Fall",
    elements: [...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true })],
  };

  const civilServiceLawBase = {
    isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Fall",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
      }),
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.client.birthDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: CivilServiceLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.civilServiceStatus`,
        isDisabled: ({ product }: { product: CivilServiceLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: CivilServiceLaw }) =>
          product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
        options: () => [{ value: "active" }, { value: "retired" }],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.personnelNumber`,
        isHidden: ({ product }: { product: CivilServiceLaw }) =>
          product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
        isDisabled: ({ product }: { product: CivilServiceLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
      },
    ],
  };

  const opposingParty =
    product.caseCategory === "advisory"
      ? {}
      : {
          isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
          label: "Gegenseite",
          elements: [
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.employerType`,
              isDisabled: () => true,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              options: () => [{ value: "federal" }, { value: "state" }, { value: "municipal" }],
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.employerState`,
              isDisabled: () => true,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED ||
                product.caseCategory === "advisory" ||
                product.employerType !== "state",
              options: () => [
                { value: "BW" },
                { value: "BY" },
                { value: "BE" },
                { value: "BB" },
                { value: "HB" },
                { value: "HH" },
                { value: "HE" },
                { value: "MV" },
                { value: "NI" },
                { value: "NW" },
                { value: "RP" },
                { value: "SL" },
                { value: "SN" },
                { value: "ST" },
                { value: "SH" },
                { value: "TH" },
              ],
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.employerMunicipality`,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED ||
                product.caseCategory === "advisory" ||
                product.employerType !== "municipal",
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.workLocationPostalCode`,
              isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: () => true,
            },
          ],
        };

  const authorityCustomer =
    product.caseCategory === "advisory"
      ? {}
      : {
          isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
          label: "Behörde laut Mandantschaft",
          elements: [
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveClient`,
              isDisabled: () => true,
              isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
              options: () => [{ value: "moneyAndRefund" }, { value: "careerAndEmployment" }],
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.authorityName`,
              isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: () => true,
            },
            ...addressFields({
              path: `${product.productClassName}.authorityAddress`,
              isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: () => true,
            }),
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.authorityTelephone`,
              isHidden: ({ product }: { product: CivilServiceLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
              isDisabled: () => true,
            },
          ],
        };

  let extraJudicialProcedureSection = extraJudicialProcedure(
    product,
    currentUser,
    [],
    getCivilServiceLawCaseRejectionCategorySelectOptions,
    (product: CivilServiceLaw): SelectOption[] => [
      { value: "agreementDone" },
      { value: "insufficientEvidence" },
      { value: "insufficientChancesOfSuccess" },
      { value: "costBenefitConsiderations" },
      { value: "anonymousOpponent" },
      { value: "opponentUnableToPay" },
      { value: "lawsuit" },
      // { value: "failureToAct" }, // TODO: will be used later when failure to act is part of the automatic process
      { value: "individualText" },
    ],
  );

  extraJudicialProcedureSection.elements = insertAfterPath(
    extraJudicialProcedureSection.elements,
    [
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeProceedingsLetterSentDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: CivilServiceLaw }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeProceedingsLetterSentDeadline`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: CivilServiceLaw }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeDecisionReceivedDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: CivilServiceLaw }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.hasFiledObjectionToAdministrativeDecision`,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
        isDisabled: ({ product }: { product: CivilServiceLaw }) => product.stage >= stagesCase.STAGE_PREPARE_LAWSUIT,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.hasFiledObjectionToAdministrativeDecision`),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.administrativeDecisionRecommendation`,
        isDisabled: ({ product }: { product: CivilServiceLaw }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage >= stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED,
        isHidden: ({ product }: { product: CivilServiceLaw }) => product.advisoryHandling === true,
        options: () => [{ value: "objection" }, { value: "accept" }, { value: "individualText" }],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.administrativeDecisionRecommendationText`,
        isDisabled: ({ product }: { product: CivilServiceLaw }) =>
          !(
            product.stage === stagesCase.STAGE_ADMINISTRATIVE_PROCEEDINGS ||
            (product.stage === stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
              product.administrativeDecisionRecommendationText)
          ),
        isHidden: ({ product }: { product: CivilServiceLaw }) =>
          !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
          (product.stage > stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
            !product.administrativeDecisionRecommendationText),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
    `${product.productClassName}.caseRejectionCategory`,
  );

  return {
    product: {
      sections: [
        incidentBaseCreated,
        civilServiceLawBase,
        aiConcernDescription(product),
        getAdditionalClientsForm(product),
        getUnderageClientForm(product, "Minderjährige Mandantschaft"),
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.timeBarredDate`,
              validators: dateFieldDefault,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling === true,
              isDisabled: ({ product }: { product: CivilServiceLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: CivilServiceLaw }) =>
                product.stage! > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedureSection,
        judicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: CivilServiceLaw }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        opposingParty,
        authorityCustomer,
        processParticipantsAuthority(product),
        opponentLawyer(product),
        caseInsurance(product),
        prosecution(product),
      ],
    },
    additionalProcessParticipants: {
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      sections: [
        {
          elements: [
            {
              type: "CourtPicker",
              path: `${product.productClassName}.responsibleCourt`,
              isDisabled: ({ product }: { product: CivilServiceLaw }) =>
                (product.caseCategory === "enforceClaim" && product.stage! < stagesCase.STAGE_ACTION_REQUIRED) ||
                hasActiveCourtAppointment(product.courtAppointments),
              additionalProps: {
                fullWidth: true,
                courtTypes: [
                  "Amtsgericht",
                  "Arbeitsgericht",
                  "Landgericht",
                  "Mahngericht",
                  "Oberlandesgericht",
                  "Verwaltungsgericht",
                ],
              },
            },
            ...allDataPageCourtFields(product.productClassName, "responsibleCourt"),
          ],
        },
      ],
    },
  };
};
